import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { cx } from '@linaria/core';
import { IconSearch } from 'components/Icons';
import { InputProps as InputPropsReactkit } from 'reakit/Input';
import { Input } from 'components/Input';
import useForwardedRef from 'hooks/useForwardedRef';
import { searchInputWrapper, searchInput, searchIconWrapper } from './SearchInput.styles';

type SearchInputProps = InputPropsReactkit & {
  iconColor?: string;
  iconSize?: number;
  icon?: React.ReactNode;
  onkeydown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, className, icon, iconSize = 23, iconColor = '#344054', autoFocus = false, onkeydown, ...rest }, forwardedRef) => {
    const { placeholder, onChange } = rest;
    const ref = useForwardedRef(forwardedRef);
    const [isFocused, setIsFocused] = useState(autoFocus);

    useEffect(() => {
      if (ref.current && isFocused) {
        ref.current.focus();
      }
    }, [ref.current, isFocused]);

    const onFocus = useCallback(() => {
      setIsFocused(true);
    }, []);

    const onInputBlur = useCallback(() => {
      setIsFocused(false);
    }, []);

    return (
      <div className={cx(searchInputWrapper, isFocused && 'focused')} tabIndex={-1} onFocus={onFocus} role="presentation">
        <div className={searchIconWrapper}>{icon || <IconSearch size={iconSize} color={iconColor} data-testid="icon-search" />}</div>
        <Input
          className={cx(searchInput, className)}
          type="text"
          value={value}
          ref={ref}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onInputBlur}
          onKeyDown={(e) => {
            e.stopPropagation();
            onkeydown?.(e);
          }}
        />
      </div>
    );
  },
);
